import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { register } from '../../services/AuthService';
import { validateInviteCode } from '../../services/InviteCodesService';
import '../Login/Login.css';

function Register() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialCode = searchParams.get('code') || '';

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [inviteCode, setInviteCode] = useState(initialCode);
    const [error, setError] = useState('');
    const [validatingCode, setValidatingCode] = useState(false);
    const [codeValid, setCodeValid] = useState(!!initialCode);
    const [codeError, setCodeError] = useState('');

    async function onValidateCode(event) {
        event.preventDefault();
        if (!inviteCode) {
            setCodeError('Por favor, informe o código de convite');
            return;
        }

        setValidatingCode(true);
        setCodeError('');

        try {
            await validateInviteCode(inviteCode);
            setCodeValid(true);
        } catch (err) {
            setCodeError(err.response?.data || 'Código de convite inválido');
            setCodeValid(false);
        } finally {
            setValidatingCode(false);
        }
    }

    function onChangeInput(event) {
        const id = event.target.id;
        const value = event.target.value;

        if (id === 'name') setName(value);
        else if (id === 'email') setEmail(value);
        else if (id === 'password') setPassword(value);
        else if (id === 'confirmPassword') setConfirmPassword(value);
        else if (id === 'inviteCode') {
            setInviteCode(value);
            setCodeValid(false);
            setCodeError('');
        }
    }

    async function onSubmit(event) {
        event.preventDefault();
        setError('');

        if (!codeValid) {
            setError('Por favor, valide o código de convite primeiro');
            return;
        }

        if (password !== confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        if (password.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres');
            return;
        }

        try {
            await register(name, email, password, inviteCode);
            navigate('/?registered=true');
        } catch (err) {
            setError(err.response?.data || 'Erro ao criar conta. Tente novamente.');
        }
    }

    if (!codeValid) {
        return (
            <main className="login-page">
                <div className="login-wrapper">
                    <div className="login-card">
                        <div className="login-logo">
                            <img src="/img/favicon/mstile-150x150.png" alt="EasyCripto" />
                            <h1 className="login-title">EasyCripto</h1>
                            <p className="login-subtitle">Digite seu código de convite para criar sua conta</p>
                        </div>
                        <form onSubmit={onValidateCode} className="login-form">
                            <div className="form-group mb-3">
                                <label htmlFor="inviteCode" className="text-gray-400 small mb-1">Código de Convite</label>
                                <div className="login-input-group">
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="EXEMPLO1234" 
                                        id="inviteCode" 
                                        value={inviteCode}
                                        onChange={onChangeInput}
                                        autoFocus 
                                        required 
                                        style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}
                                    />
                                </div>
                                {codeError && <div className="alert alert-danger mt-2 mb-0 login-error">{codeError}</div>}
                            </div>
                            <div className="d-grid mb-2">
                                <button type="submit" className="btn login-button" disabled={validatingCode}>
                                    {validatingCode ? 'Validando...' : 'Validar Código'}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="login-bottom-card">
                        <span>
                            Já tem conta?
                            <Link to="/" className="ms-1">Entrar</Link>
                        </span>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main>
            <section className="vh-lg-100 mt-5 mt-lg-0 bg-soft d-flex align-items-center">
                <div className="container">
                    <p className="text-center">
                        <Link to="/" className="d-flex align-items-center justify-content-center">
                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd"></path>
                            </svg>
                            Voltar para login
                        </Link>
                    </p>
                    <div className="col-12 d-flex align-items-center justify-content-center">
                        <div className="bg-white shadow border-0 rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                            <div className="text-center">
                                <img src="/img/favicon/mstile-150x150.png" alt="Beholder" width={64} />
                            </div>
                            <div className="text-center text-md-center mb-4 mt-md-0">
                                <h1 className="mb-0 h3">Criar Conta</h1>
                                <p className="text-muted mt-2">Código de convite: <strong>{inviteCode}</strong></p>
                            </div>
                            <form action="#" className="mt-4" onSubmit={onSubmit}>
                                <div className="form-group mb-4">
                                    <label htmlFor="name">Nome Completo</label>
                                    <div className="input-group">
                                        <span className="input-group-text" id="basic-addon1">
                                            <svg className="icon icon-xs text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                            </svg>
                                        </span>
                                        <input type="text" className="form-control" placeholder="Seu nome completo" id="name" autoFocus required onChange={onChangeInput} />
                                    </div>
                                </div>
                                <div className="form-group mb-4">
                                    <label htmlFor="email">Seu e-mail</label>
                                    <div className="input-group">
                                        <span className="input-group-text" id="basic-addon1">
                                            <svg className="icon icon-xs text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                                            </svg>
                                        </span>
                                        <input type="email" className="form-control" placeholder="exemplo@empresa.com" id="email" required onChange={onChangeInput} />
                                    </div>
                                </div>
                                <div className="form-group mb-4">
                                    <label htmlFor="password">Senha</label>
                                    <div className="input-group">
                                        <span className="input-group-text" id="basic-addon2">
                                            <svg className="icon icon-xs text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                                            </svg>
                                        </span>
                                        <input type="password" placeholder="Mínimo 6 caracteres" className="form-control" id="password" required onChange={onChangeInput} />
                                    </div>
                                </div>
                                <div className="form-group mb-4">
                                    <label htmlFor="confirmPassword">Confirmar Senha</label>
                                    <div className="input-group">
                                        <span className="input-group-text" id="basic-addon3">
                                            <svg className="icon icon-xs text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                                            </svg>
                                        </span>
                                        <input type="password" placeholder="Repita a senha" className="form-control" id="confirmPassword" required onChange={onChangeInput} />
                                    </div>
                                </div>
                                <div className="d-grid">
                                    <button type="submit" className="btn btn-gray-800">Criar Conta</button>
                                </div>
                                {
                                    error ?
                                        <div className="alert alert-danger mt-2">{error}</div>
                                        : <></>
                                }
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

export default Register;


